import { useMutation, useQuery } from '@apollo/client/react';
import { Button, ButtonProps, Icon } from '@chakra-ui/react';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { ScoUserMatch } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { useCallback, useMemo, useState } from 'react';
import { LuCheck, LuStar } from 'react-icons/lu';
export const UserMatchSaveButton = (props: {
  userMatchId: string;
  buttonProps?: ButtonProps;
}) => {
  const {
    currentUser
  } = useCurrentUser();
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const query = useQuery(gql`
      query ($userMatchId: String!) {
        scoUserMatch(userMatchId: $userMatchId) {
          id
          userMatchId
          searchingUserId
          targetUserId
          savedBySearchingUserAt
          savedByTargetUserAt
        }
      }
    `, {
    variables: {
      userMatchId: props.userMatchId
    }
  });
  const [upsertUserMatch] = useMutation(gql`
    mutation ($input: UpsertUserMatchInput!) {
      upsertUserMatch(input: $input) {
        userMatch {
          id
          savedByTargetUserAt
          savedBySearchingUserAt
        }
      }
    }
  `);
  const userMatch: ScoUserMatch = query.data?.scoUserMatch;
  const savedByFieldKey = useMemo(() => {
    if (currentUser?.userId === userMatch?.searchingUserId) {
      return 'savedBySearchingUserAt';
    }
    if (currentUser?.userId === userMatch?.targetUserId) {
      return 'savedByTargetUserAt';
    }
    return null;
  }, [currentUser?.userId, userMatch?.searchingUserId, userMatch?.targetUserId]);
  const savedAt = useMemo(() => {
    return userMatch && savedByFieldKey && userMatch[savedByFieldKey];
  }, [userMatch, savedByFieldKey]);
  const onClick = useCallback(async ev => {
    ev.preventDefault();
    if (!savedByFieldKey) {
      alert('Error: Invalid save key.');
      return;
    }
    setIsLoading(true);
    await upsertUserMatch({
      variables: {
        input: {
          userMatchId: props.userMatchId,
          [savedByFieldKey]: savedAt ? null : new Date()
        }
      }
    });
    setIsLoading(false);
  }, [savedByFieldKey, savedAt, props.userMatchId]);
  return <Button bgColor={savedAt ? 'green' : 'transparent'} disabled={isLoading || query.loading} loading={isLoading || query.loading} size={getBreakpointValue(['xs', 'xs', 'sm'])} variant={savedAt ? 'solid' : 'outline'} onClick={onClick} {...props.buttonProps}>
      <Icon as={savedAt ? LuCheck : LuStar} />
      {savedAt ? 'Saved' : 'Save'}
    </Button>;
};